import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import axios from 'axios';
import {
  Pencil,
  Plus,
  ShieldCheck,
  Tablet,
  Sun,
  Moon,
  RefreshCw,
  Lock,
  Trash2,
  Building2,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';
import { get, post, patch, del, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { TabsPills } from '@/components/ui/tabs-pills';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { formatDateTime } from '@/lib/format';
import type { GuardShift } from '@hooddesk/shared-types';
import type { Guard, SecurityAgency, GuardEmploymentType } from './_agency-types';

interface Gate {
  id: string;
  name: string;
}
interface Block {
  id: string;
  name: string;
}

interface GuardListResponse {
  items: Guard[];
  next_cursor?: string | null;
}

const SHIFT_META: Record<GuardShift, { label: string; icon: JSX.Element; tone: string }> = {
  day: {
    label: 'Day',
    icon: <Sun className="h-3.5 w-3.5" strokeWidth={1.75} />,
    tone: 'bg-[color:var(--vt-warn-100)] text-[color:var(--vt-warn)]'
  },
  night: {
    label: 'Night',
    icon: <Moon className="h-3.5 w-3.5" strokeWidth={1.75} />,
    tone: 'bg-vt-primary-100 text-vt-primary'
  },
  rotating: {
    label: 'Rotating',
    icon: <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.75} />,
    tone: 'bg-vt-secondary-100 text-vt-secondary'
  }
};

// Form schema extends the original V0.1 schema with Tier-1 fields:
//   employment_type, agency_id, agency_employee_id, hire_date, contract_until
//   + personal (DOB, blood_group, addresses, emergency contact)
//   + in-house finance fields (salary, PF, ESIC, bank, KYC last4)
//
// Conditional validation: when employment_type=agency, agency_id is required;
// in-house finance fields are *optional* across both — society admins can
// hold them blank during onboarding and fill in later.
const CreateGuardSchema = z
  .object({
    full_name: z.string().min(2, 'Name required').max(120),
    phone: z.string().regex(/^\+?[1-9]\d{6,14}$/, 'E.164 format (e.g. +9180…)'),
    shift: z.enum(['day', 'night', 'rotating']).default('rotating'),
    employee_code: z.string().max(40).optional(),
    duty_kind: z.enum(['', 'gate', 'tower', 'patrol', 'common_area']).optional(),
    gate_id: z.string().optional(),
    block_id: z.string().optional(),

    employment_type: z.enum(['in_house', 'agency']).default('in_house'),
    agency_id: z.string().optional(),
    agency_employee_id: z.string().max(80).optional(),
    hire_date: z.string().optional(),
    contract_until: z.string().optional(),

    date_of_birth: z.string().optional(),
    blood_group: z.string().max(8).optional(),
    permanent_address: z.string().max(500).optional(),
    current_address: z.string().max(500).optional(),
    emergency_contact_name: z.string().max(120).optional(),
    emergency_contact_phone: z.string().max(20).optional(),

    base_salary_inr: z.coerce.number().int().min(0).max(10_000_000).optional(),
    pf_account: z.string().max(60).optional(),
    esic_account: z.string().max(60).optional(),
    bank_name: z.string().max(120).optional(),
    bank_account_last4: z.string().max(10).optional(),
    bank_ifsc: z.string().max(20).optional(),
    pan_last4: z.string().max(10).optional(),
    aadhaar_last4: z.string().max(10).optional()
  })
  .refine(
    (d) => d.duty_kind !== 'gate' || (d.gate_id && d.gate_id.length > 0),
    { message: 'Gate is required when duty kind is Gate', path: ['gate_id'] }
  )
  .refine(
    (d) => d.duty_kind !== 'tower' || (d.block_id && d.block_id.length > 0),
    { message: 'Tower is required when duty kind is Tower', path: ['block_id'] }
  )
  .refine(
    (d) => d.employment_type !== 'agency' || (d.agency_id && d.agency_id.length > 0),
    { message: 'Pick an agency for agency-hired guards', path: ['agency_id'] }
  );
type CreateGuardForm = z.infer<typeof CreateGuardSchema>;

const EMPTY_FORM: CreateGuardForm = {
  full_name: '',
  phone: '',
  shift: 'rotating',
  employee_code: '',
  duty_kind: '',
  gate_id: '',
  block_id: '',
  employment_type: 'in_house',
  agency_id: '',
  agency_employee_id: '',
  hire_date: '',
  contract_until: '',
  date_of_birth: '',
  blood_group: '',
  permanent_address: '',
  current_address: '',
  emergency_contact_name: '',
  emergency_contact_phone: '',
  base_salary_inr: 0,
  pf_account: '',
  esic_account: '',
  bank_name: '',
  bank_account_last4: '',
  bank_ifsc: '',
  pan_last4: '',
  aadhaar_last4: ''
};

export function GuardsPage(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<Guard | null>(null);
  const [employmentFilter, setEmploymentFilter] = useState<'all' | GuardEmploymentType>('all');
  const [agencyFilter, setAgencyFilter] = useState<string>('');
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['guards', employmentFilter, agencyFilter],
    queryFn: () =>
      get<GuardListResponse>('/guards', {
        params: {
          ...(employmentFilter !== 'all' ? { employment_type: employmentFilter } : {}),
          ...(employmentFilter === 'agency' && agencyFilter ? { agency_id: agencyFilter } : {})
        }
      }),
    retry: (failureCount, err) => {
      if (axios.isAxiosError(err) && err.response?.status === 403) return false;
      return failureCount < 2;
    }
  });

  const editingGuard = useMemo(
    () => (editingId ? query.data?.items.find((g) => g.id === editingId) ?? null : null),
    [editingId, query.data]
  );

  // Side-fetches for dropdowns (gates / blocks / agencies). Enabled only when
  // the form dialog is open so we don't pay for them on every guards visit.
  const gatesQuery = useQuery({
    queryKey: ['society', 'gates'],
    queryFn: () => get<{ items: Gate[] }>('/society/gates'),
    enabled: open
  });
  const blocksQuery = useQuery({
    queryKey: ['society', 'blocks'],
    queryFn: () => get<{ items: Block[] }>('/society/blocks'),
    enabled: open
  });
  // Active agencies for the form's agency selector AND for the filter row.
  const agenciesQuery = useQuery({
    queryKey: ['agencies', 'active'],
    queryFn: () =>
      get<{ items: SecurityAgency[]; next_cursor?: string | null }>('/agencies', {
        params: { is_active: true, limit: 200 }
      })
  });
  const agencies = agenciesQuery.data?.items ?? [];

  const form = useForm<CreateGuardForm>({
    resolver: zodResolver(CreateGuardSchema),
    defaultValues: EMPTY_FORM
  });

  const dutyKind = form.watch('duty_kind');
  const employmentType = form.watch('employment_type');

  // Convert form values → submit body. Empty strings → undefined so the
  // backend never sees blanks; numeric coercion handled by Zod.
  const buildPayload = (values: CreateGuardForm) => {
    const isAgency = values.employment_type === 'agency';
    return {
      full_name: values.full_name,
      phone: values.phone,
      shift: values.shift,
      employee_code: values.employee_code?.trim() ? values.employee_code : undefined,
      duty_kind: values.duty_kind ? values.duty_kind : undefined,
      gate_id: values.duty_kind === 'gate' && values.gate_id ? values.gate_id : undefined,
      block_id: values.duty_kind === 'tower' && values.block_id ? values.block_id : undefined,

      employment_type: values.employment_type,
      agency_id: isAgency && values.agency_id ? values.agency_id : undefined,
      agency_employee_id:
        isAgency && values.agency_employee_id?.trim()
          ? values.agency_employee_id
          : undefined,
      hire_date: values.hire_date || undefined,
      contract_until: isAgency && values.contract_until ? values.contract_until : undefined,

      date_of_birth: values.date_of_birth || undefined,
      blood_group: values.blood_group?.trim() || undefined,
      permanent_address: values.permanent_address?.trim() || undefined,
      current_address: values.current_address?.trim() || undefined,
      emergency_contact_name: values.emergency_contact_name?.trim() || undefined,
      emergency_contact_phone: values.emergency_contact_phone?.trim() || undefined,

      // In-house only finance fields. For agency employees, hide entirely.
      base_salary_inr: !isAgency ? values.base_salary_inr ?? undefined : undefined,
      pf_account: !isAgency ? values.pf_account?.trim() || undefined : undefined,
      esic_account: !isAgency ? values.esic_account?.trim() || undefined : undefined,
      bank_name: !isAgency ? values.bank_name?.trim() || undefined : undefined,
      bank_account_last4: !isAgency ? values.bank_account_last4?.trim() || undefined : undefined,
      bank_ifsc: !isAgency ? values.bank_ifsc?.trim() || undefined : undefined,
      pan_last4: !isAgency ? values.pan_last4?.trim() || undefined : undefined,
      aadhaar_last4: !isAgency ? values.aadhaar_last4?.trim() || undefined : undefined
    };
  };

  const create = useMutation({
    mutationFn: (values: CreateGuardForm) =>
      post<Guard, ReturnType<typeof buildPayload>>('/guards', buildPayload(values)),
    onSuccess: (g) => {
      toast.success(`${g.full_name} added to guard roster`);
      void queryClient.invalidateQueries({ queryKey: ['guards'] });
      setOpen(false);
      form.reset(EMPTY_FORM);
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const remove = useMutation({
    mutationFn: (id: string) => del<void>(`/guards/${id}`),
    onSuccess: () => {
      toast.success('Guard removed');
      void queryClient.invalidateQueries({ queryKey: ['guards'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const update = useMutation({
    mutationFn: ({ id, values }: { id: string; values: CreateGuardForm }) =>
      patch<Guard>(`/guards/${id}`, buildPayload(values)),
    onSuccess: (g) => {
      toast.success(`${g.full_name} updated`);
      void queryClient.invalidateQueries({ queryKey: ['guards'] });
      setEditingId(null);
      setOpen(false);
      form.reset(EMPTY_FORM);
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  useEffect(() => {
    if (editingGuard && open) {
      form.reset({
        full_name: editingGuard.full_name,
        phone: editingGuard.phone,
        shift: editingGuard.shift,
        employee_code: editingGuard.employee_code ?? '',
        duty_kind: editingGuard.duty_kind ?? '',
        gate_id: editingGuard.gate_id ?? '',
        block_id: editingGuard.block_id ?? '',
        employment_type: editingGuard.employment_type ?? 'in_house',
        agency_id: editingGuard.agency_id ?? '',
        agency_employee_id: editingGuard.agency_employee_id ?? '',
        hire_date: editingGuard.hire_date ? editingGuard.hire_date.slice(0, 10) : '',
        contract_until: editingGuard.contract_until ? editingGuard.contract_until.slice(0, 10) : '',
        date_of_birth: editingGuard.date_of_birth ? editingGuard.date_of_birth.slice(0, 10) : '',
        blood_group: editingGuard.blood_group ?? '',
        permanent_address: editingGuard.permanent_address ?? '',
        current_address: editingGuard.current_address ?? '',
        emergency_contact_name: editingGuard.emergency_contact_name ?? '',
        emergency_contact_phone: editingGuard.emergency_contact_phone ?? '',
        base_salary_inr: editingGuard.base_salary_inr ?? 0,
        pf_account: editingGuard.pf_account ?? '',
        esic_account: editingGuard.esic_account ?? '',
        bank_name: editingGuard.bank_name ?? '',
        bank_account_last4: editingGuard.bank_account_last4 ?? '',
        bank_ifsc: editingGuard.bank_ifsc ?? '',
        pan_last4: editingGuard.pan_last4 ?? '',
        aadhaar_last4: editingGuard.aadhaar_last4 ?? ''
      });
    } else if (!open && !editingId) {
      form.reset(EMPTY_FORM);
    }
  }, [editingGuard, open, editingId, form]);

  const onSubmit = (v: CreateGuardForm) => {
    if (editingId) update.mutate({ id: editingId, values: v });
    else create.mutate(v);
  };

  const closeDialog = () => {
    setOpen(false);
    setEditingId(null);
  };

  const guards = query.data?.items ?? [];
  const activeCount = guards.filter((g) => g.active).length;
  const pairedCount = guards.filter((g) => g.device_id).length;
  const agencyCount = guards.filter((g) => g.employment_type === 'agency').length;

  const isForbidden =
    axios.isAxiosError(query.error) && query.error.response?.status === 403;

  const formDialog = (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : closeDialog())}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          disabled={isForbidden}
          title={isForbidden ? 'Admin role required' : undefined}
          onClick={() => setEditingId(null)}
        >
          <Plus className="h-4 w-4" /> Add guard
        </Button>
      </DialogTrigger>
      <DialogContent className="!max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editingId ? 'Edit guard' : 'Add guard'}</DialogTitle>
          <DialogDescription>
            {editingId
              ? 'Update guard details. Phone change re-issues OTP login.'
              : 'Onboard a new guard. They will receive an OTP login on the phone provided.'}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-vt-4"
        >
          {/* ── Employment type toggle ── */}
          <fieldset className="flex flex-col gap-vt-2 rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3">
            <legend className="px-vt-1 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
              Employment type
            </legend>
            <div className="flex flex-wrap items-center gap-vt-4">
              <label className="flex items-center gap-vt-2 text-vt-13 text-vt-text">
                <input
                  type="radio"
                  value="in_house"
                  {...form.register('employment_type')}
                  className="accent-vt-primary"
                />
                In-house (society employs directly)
              </label>
              <label className="flex items-center gap-vt-2 text-vt-13 text-vt-text">
                <input
                  type="radio"
                  value="agency"
                  {...form.register('employment_type')}
                  className="accent-vt-primary"
                />
                Agency (outsourced)
              </label>
            </div>
          </fieldset>

          {/* ── Identity ── */}
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="full_name">Full name</Label>
            <Input
              id="full_name"
              placeholder="Ramesh Kumar"
              autoFocus
              aria-invalid={Boolean(form.formState.errors.full_name) || undefined}
              {...form.register('full_name')}
            />
            {form.formState.errors.full_name ? (
              <p className="text-vt-12 text-vt-alert" role="alert">{form.formState.errors.full_name.message}</p>
            ) : null}
          </div>
          <div className="grid grid-cols-2 gap-vt-3">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                placeholder="+919812345678"
                aria-invalid={Boolean(form.formState.errors.phone) || undefined}
                {...form.register('phone')}
              />
              {form.formState.errors.phone ? (
                <p className="text-vt-12 text-vt-alert" role="alert">{form.formState.errors.phone.message}</p>
              ) : (
                <p className="text-vt-12 text-vt-text-soft">E.164 format with country code.</p>
              )}
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="shift">Shift</Label>
              <Select id="shift" {...form.register('shift')}>
                <option value="day">Day</option>
                <option value="night">Night</option>
                <option value="rotating">Rotating</option>
              </Select>
            </div>
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="employee_code">Employee # (optional)</Label>
            <Input
              id="employee_code"
              placeholder="G-005"
              {...form.register('employee_code')}
            />
          </div>

          {/* ── Agency block (only when employment_type=agency) ── */}
          {employmentType === 'agency' ? (
            <fieldset className="flex flex-col gap-vt-3 rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3">
              <legend className="px-vt-1 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
                Agency
              </legend>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="agency_id">Security agency</Label>
                <Select
                  id="agency_id"
                  disabled={agenciesQuery.isLoading}
                  {...form.register('agency_id')}
                >
                  <option value="">
                    {agenciesQuery.isLoading ? 'Loading agencies…' : 'Select agency'}
                  </option>
                  {agencies.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </Select>
                {form.formState.errors.agency_id ? (
                  <p className="text-vt-12 text-vt-alert" role="alert">
                    {form.formState.errors.agency_id.message}
                  </p>
                ) : agencies.length === 0 && !agenciesQuery.isLoading ? (
                  <p className="text-vt-12 text-vt-warn">
                    No agencies on file yet —{' '}
                    <Link to="/agencies" className="underline">
                      add an agency
                    </Link>{' '}
                    first.
                  </p>
                ) : null}
              </div>
              <div className="grid grid-cols-2 gap-vt-3">
                <div className="flex flex-col gap-vt-1">
                  <Label htmlFor="agency_employee_id">Agency employee #</Label>
                  <Input id="agency_employee_id" placeholder="SG-0123" {...form.register('agency_employee_id')} />
                </div>
                <div className="flex flex-col gap-vt-1">
                  <Label htmlFor="contract_until">Contract until</Label>
                  <Input id="contract_until" type="date" {...form.register('contract_until')} />
                </div>
              </div>
            </fieldset>
          ) : null}

          {/* ── Common: hire date ── */}
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="hire_date">Hire date</Label>
            <Input id="hire_date" type="date" {...form.register('hire_date')} />
          </div>

          {/* ── Personal & emergency ── */}
          <fieldset className="flex flex-col gap-vt-3 rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3">
            <legend className="px-vt-1 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
              Personal &amp; emergency
            </legend>
            <div className="grid grid-cols-2 gap-vt-3">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="date_of_birth">Date of birth</Label>
                <Input id="date_of_birth" type="date" {...form.register('date_of_birth')} />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="blood_group">Blood group</Label>
                <Input id="blood_group" placeholder="O+" {...form.register('blood_group')} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-vt-3">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="emergency_contact_name">Emergency contact name</Label>
                <Input id="emergency_contact_name" {...form.register('emergency_contact_name')} />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="emergency_contact_phone">Emergency contact phone</Label>
                <Input id="emergency_contact_phone" {...form.register('emergency_contact_phone')} />
              </div>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="permanent_address">Permanent address</Label>
              <Input id="permanent_address" {...form.register('permanent_address')} />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="current_address">Current address</Label>
              <Input id="current_address" {...form.register('current_address')} />
            </div>
          </fieldset>

          {/* ── Duty assignment ── */}
          <fieldset className="flex flex-col gap-vt-3 rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3">
            <legend className="px-vt-1 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
              Duty assignment
            </legend>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="duty_kind">Posted at</Label>
              <Select id="duty_kind" {...form.register('duty_kind')}>
                <option value="">Unassigned</option>
                <option value="gate">Gate</option>
                <option value="tower">Tower</option>
                <option value="patrol">Patrol</option>
                <option value="common_area">Common area</option>
              </Select>
            </div>
            {dutyKind === 'gate' ? (
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="gate_id">Which gate</Label>
                <Select id="gate_id" disabled={gatesQuery.isLoading} {...form.register('gate_id')}>
                  <option value="">{gatesQuery.isLoading ? 'Loading gates…' : 'Select gate'}</option>
                  {(gatesQuery.data?.items ?? []).map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </Select>
                {form.formState.errors.gate_id ? (
                  <p className="text-vt-12 text-vt-alert" role="alert">{form.formState.errors.gate_id.message}</p>
                ) : (gatesQuery.data?.items.length ?? 0) === 0 && !gatesQuery.isLoading ? (
                  <p className="text-vt-12 text-vt-warn">No gates defined yet — <a href="/gates" className="underline">add a gate</a> first.</p>
                ) : null}
              </div>
            ) : null}
            {dutyKind === 'tower' ? (
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="block_id">Which tower / block</Label>
                <Select id="block_id" disabled={blocksQuery.isLoading} {...form.register('block_id')}>
                  <option value="">{blocksQuery.isLoading ? 'Loading…' : 'Select tower'}</option>
                  {(blocksQuery.data?.items ?? []).map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </Select>
                {form.formState.errors.block_id ? (
                  <p className="text-vt-12 text-vt-alert" role="alert">{form.formState.errors.block_id.message}</p>
                ) : null}
              </div>
            ) : null}
          </fieldset>

          {/* ── In-house finance (hidden for agency hires) ── */}
          {employmentType === 'in_house' ? (
            <fieldset className="flex flex-col gap-vt-3 rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3">
              <legend className="px-vt-1 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
                Salary, statutory &amp; bank
              </legend>
              <div className="grid grid-cols-2 gap-vt-3">
                <div className="flex flex-col gap-vt-1">
                  <Label htmlFor="base_salary_inr">Base salary (₹/month)</Label>
                  <Input id="base_salary_inr" type="number" min={0} {...form.register('base_salary_inr')} />
                </div>
                <div className="flex flex-col gap-vt-1">
                  <Label htmlFor="pf_account">PF account #</Label>
                  <Input id="pf_account" {...form.register('pf_account')} />
                </div>
                <div className="flex flex-col gap-vt-1">
                  <Label htmlFor="esic_account">ESIC account #</Label>
                  <Input id="esic_account" {...form.register('esic_account')} />
                </div>
                <div className="flex flex-col gap-vt-1">
                  <Label htmlFor="bank_name">Bank name</Label>
                  <Input id="bank_name" {...form.register('bank_name')} />
                </div>
                <div className="flex flex-col gap-vt-1">
                  <Label htmlFor="bank_account_last4">Bank A/C last 4</Label>
                  <Input id="bank_account_last4" placeholder="1234" {...form.register('bank_account_last4')} />
                </div>
                <div className="flex flex-col gap-vt-1">
                  <Label htmlFor="bank_ifsc">IFSC</Label>
                  <Input id="bank_ifsc" placeholder="HDFC0001234" {...form.register('bank_ifsc')} />
                </div>
                <div className="flex flex-col gap-vt-1">
                  <Label htmlFor="pan_last4">PAN last 4</Label>
                  <Input id="pan_last4" {...form.register('pan_last4')} />
                </div>
                <div className="flex flex-col gap-vt-1">
                  <Label htmlFor="aadhaar_last4">Aadhaar last 4</Label>
                  <Input id="aadhaar_last4" {...form.register('aadhaar_last4')} />
                </div>
              </div>
            </fieldset>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={create.isPending || update.isPending}
              loadingText={editingId ? 'Saving…' : 'Adding…'}
            >
              {editingId ? 'Save changes' : 'Add guard'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  return (
    <>
      <PageHeader
        title="Guards"
        description="Manage gate staff, shifts, agency assignment, and tablet device bindings."
        actions={formDialog}
      />
      <div className="mx-auto w-full max-w-[1400px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        {/* KPI strip */}
        <div className="grid grid-cols-1 gap-vt-4 md:grid-cols-2 xl:grid-cols-4">
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-vt-primary-100 text-vt-primary">
                <ShieldCheck className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">On payroll</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {query.isLoading ? <Skeleton className="h-7 w-12" /> : guards.length}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-safe-100)] text-[color:var(--vt-safe)]">
                <ShieldCheck className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Active right now</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {query.isLoading ? <Skeleton className="h-7 w-12" /> : activeCount}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-info-100)] text-[color:var(--vt-info)]">
                <Building2 className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Agency-hired</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {query.isLoading ? <Skeleton className="h-7 w-12" /> : agencyCount}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-info-100)] text-[color:var(--vt-info)]">
                <Tablet className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Tablets paired</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {query.isLoading ? <Skeleton className="h-7 w-12" /> : pairedCount}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="flex flex-wrap items-center gap-vt-3 p-vt-4">
            <TabsPills
              value={employmentFilter}
              onChange={(v) => {
                setEmploymentFilter(v as 'all' | GuardEmploymentType);
                // Clear agency filter when switching to all/in_house.
                if (v !== 'agency') setAgencyFilter('');
              }}
              options={[
                { value: 'all', label: 'All' },
                { value: 'in_house', label: 'In-house' },
                { value: 'agency', label: 'Agency' }
              ]}
              aria-label="Employment type filter"
            />
            {employmentFilter === 'agency' ? (
              <>
                <Filter className="ml-vt-2 h-4 w-4 text-vt-text-muted" />
                <div className="inline-flex items-center gap-vt-2">
                  <Label htmlFor="agency-filter" className="text-vt-12 text-vt-text-muted">
                    Agency:
                  </Label>
                  <Select
                    id="agency-filter"
                    className="h-8 w-56 text-vt-12"
                    value={agencyFilter}
                    onChange={(e) => setAgencyFilter(e.target.value)}
                  >
                    <option value="">All agencies</option>
                    {agencies.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </Select>
                </div>
              </>
            ) : null}
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-vt-4">
            {query.isLoading ? (
              <div className="flex flex-col gap-vt-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : isForbidden ? (
              <EmptyState
                icon={<Lock className="h-6 w-6" strokeWidth={1.75} />}
                title="Admin role required"
                description="The guards directory is visible to society admins, super-admins, and guards themselves. Sign in with an admin account to manage staff."
              />
            ) : query.isError ? (
              <EmptyState
                icon={<ShieldCheck className="h-6 w-6" strokeWidth={1.75} />}
                title="Could not load guards"
                description={getErrorMessage(query.error)}
                action={
                  <Button variant="secondary" onClick={() => void query.refetch()}>
                    Try again
                  </Button>
                }
              />
            ) : guards.length === 0 ? (
              <EmptyState
                icon={<ShieldCheck className="h-6 w-6" strokeWidth={1.75} />}
                title="No guards added"
                description="Onboard guards to assign tablet device bindings, PINs, and shift schedules."
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Guard</TableHead>
                    <TableHead>Employee #</TableHead>
                    <TableHead>Employment</TableHead>
                    <TableHead>Agency</TableHead>
                    <TableHead>Shift</TableHead>
                    <TableHead>Posted at</TableHead>
                    <TableHead>Tablet</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Onboarded</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {guards.map((g) => {
                    const meta = SHIFT_META[g.shift];
                    return (
                      <TableRow key={g.id}>
                        <TableCell>
                          <div className="flex items-center gap-vt-3">
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-vt-primary-100 text-vt-12 font-semibold text-vt-primary">
                              {g.full_name.split(' ').map((s) => s[0]).join('').slice(0, 2).toUpperCase()}
                            </span>
                            <div className="min-w-0">
                              {/* Name links to the 360° profile page. */}
                              <Link
                                to="/guards/$id"
                                params={{ id: g.id }}
                                className="block truncate font-medium text-vt-text hover:text-vt-primary hover:underline"
                              >
                                {g.full_name}
                              </Link>
                              <div className="truncate text-vt-12 text-vt-text-muted">{g.phone}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-vt-13 text-vt-text-muted">
                          {g.employee_code ?? '—'}
                        </TableCell>
                        <TableCell>
                          {g.employment_type === 'agency' ? (
                            <Badge variant="info">Agency</Badge>
                          ) : (
                            <Badge variant="muted">In-house</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-vt-13 text-vt-text">
                          {g.employment_type === 'agency' && g.agency ? (
                            <Link
                              to="/agencies/$id"
                              params={{ id: g.agency.id }}
                              className="font-medium text-vt-primary hover:underline"
                            >
                              {g.agency.name}
                            </Link>
                          ) : (
                            <span className="text-vt-text-soft">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`vt-pill inline-flex items-center gap-1 ${meta.tone}`}
                          >
                            {meta.icon}
                            {meta.label}
                          </span>
                        </TableCell>
                        <TableCell>
                          {g.duty_kind === 'gate' && g.gate ? (
                            <span className="text-vt-13 text-vt-text">
                              Gate · <span className="font-medium">{g.gate.name}</span>
                            </span>
                          ) : g.duty_kind === 'tower' && g.tower ? (
                            <span className="text-vt-13 text-vt-text">
                              Tower · <span className="font-medium">{g.tower.name}</span>
                            </span>
                          ) : g.duty_kind === 'patrol' ? (
                            <span className="text-vt-13 text-vt-text-muted">Patrol</span>
                          ) : g.duty_kind === 'common_area' ? (
                            <span className="text-vt-13 text-vt-text-muted">Common area</span>
                          ) : (
                            <span className="text-vt-12 text-vt-text-soft">Unassigned</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {g.device_id ? (
                            <span className="inline-flex items-center gap-1 font-mono text-vt-12 text-vt-text-muted">
                              <Tablet className="h-3.5 w-3.5" strokeWidth={1.75} />
                              {g.device_id.slice(0, 12)}…
                            </span>
                          ) : (
                            <span className="text-vt-12 text-vt-text-soft">Unpaired</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {g.active ? (
                            <Badge variant="ok">Active</Badge>
                          ) : (
                            <Badge variant="muted">Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-vt-text-muted">{formatDateTime(g.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex items-center gap-vt-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Edit"
                              onClick={() => {
                                setEditingId(g.id);
                                setOpen(true);
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={remove.isPending}
                              title="Remove"
                              onClick={() => setRemoveTarget(g)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <ConfirmDialog
        open={Boolean(removeTarget)}
        onOpenChange={(v) => (v ? null : setRemoveTarget(null))}
        title="Remove guard"
        description={
          removeTarget ? (
            <>
              Remove <strong>{removeTarget.full_name}</strong> from the guard roster? They lose
              gate-app access immediately.
            </>
          ) : null
        }
        confirmLabel="Remove guard"
        variant="danger"
        loading={remove.isPending}
        onConfirm={() => {
          if (!removeTarget) return;
          remove.mutate(removeTarget.id, { onSuccess: () => setRemoveTarget(null) });
        }}
      />
    </>
  );
}
